import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./index.scss";
import { useDispatch, useSelector } from "react-redux";
import { GET_CASE_CLAIM_DETAILS } from "../services/get-case-claim-details";
import { first, isEqual } from "lodash";
import MdaeDataGrid, {
  IMdaeDataGridProps,
} from "../../../common_components/common_widgets/data-grid";
import MdaeNumericBox from "../../../common_components/common_widgets/numeric-box";
import MdaeTextBox from "../../../common_components/common_widgets/text-box";
import { codeValueTemplate, diagnosisConfigs, getLinesProfessionalExtraColumns, getlinesInOutExtraColumns, linesConfig } from "./constants";
import {
  initialCaseFieldsTabFlags,
  SET_AUDIT_CASE_LINES,
  SET_CASE_FIELDS_TAB_FLAGS,
  TOGGLE_CASE_SUMMARY_DRAWER,
  TOGGLE_LINE_DATAGRID_SHOW_ALL_FIELDS_VIEW,
} from "../reducers/case-fields-reducer";
import MdaeButton from "../../common_widgets/button";
import MdaeDateBox from "../../common_widgets/date-box";
import MdaeScrollView from "../../common_widgets/scroll-view";
import LinkedAuditItems from "../linked-audit-items";
import { toggleBillHistoryPopupVisible } from "../../../pages/external-audit/case-list/bill-history";
import { isManualCase } from "../../../pages/external-audit/case-detail/component";
import { toggleDenialPredictorResponseGridPopup } from "../../../pages/external-audit/case-detail/denial-predictor-response-grid";
import {
  SET_RESPONSE_ALERT_DATA,
  TOGGLE_LINE_LEVEL_DENIAL_PREDICTOR_POPOVER,
} from "../../../pages/analyzer/denials-predictor/reducers/denials-predictor-reducer";
import MdaePopover from "../../common_widgets/popover";
import { assignVersionToList, checkListIsEqual } from "../../mdae-framework";
import { SET_COMPLETE_AUDIT_CASE_DETAIL } from "../../../pages/external-audit/case-detail/reducers/case-detail-reducer";
import dxDataGrid from "devextreme/ui/data_grid";
import MdaeCheckBox from "../../common_widgets/check-box";
import { SET_CASE_FORM_DATA, SET_LINE_CELL_CLICKED, SET_TARGET_TYPE_VALUE_LIST } from "../../../pages/external-audit/case-detail/case-data/reducers/case-data-reducer";
import { TOGGLE_CONFIRMATION_POPUP } from "../../mdae-common-reducer";
import { ViewRevLineComponentCheckboxComponent } from "../../../pages/external-audit/case-list/case-list-detail";

const GRIDNAME = {DIAGNOSIS: "DIAGNOSIS", LINE: "LINE"};
interface ICaseFields {
  isHideBillingHistoryLink?: boolean;
  isFromCasePreview?: boolean;
  isFromExternalAudit?: boolean;
  isDenialPredictorFlagAvailable?: boolean;
  isCheckBoxVisibleForCharges?: boolean;
}

  const getAllRoundCharges = (caseData) => {
    const tempRoundCharges = [];
    if (Array.isArray(caseData?.roundData)) {
      caseData.roundData.forEach((round) => {
        if (Array.isArray(round.roundCharges)) {
          tempRoundCharges.push(...round.roundCharges);
        }
      });
    }
    return tempRoundCharges;
  };

  const linesCheckboxCellRender = (
    cellData,
    isCheckBoxVisibleForCharges,
    getAllRoundCharges,
    toggleConfirmationPopupVisible
  ) => (dispatch, getState) => {
    const {
      cptHcpcs,
      procedureModifier1,
      procedureModifier2,
      procedureModifier3,
      procedureModifier4,
    } = cellData.data;
    const hasCode =
      !!cptHcpcs ||
      !!procedureModifier1 ||
      !!procedureModifier2 ||
      !!procedureModifier3 ||
      !!procedureModifier4;
    if (!hasCode) return null; // Do not render checkbox
    const targetTypeSelectedList = getState()["caseDataReducer"]
      .targetTypeSelectedList;
    const caseDetailData = getState()["caseDetailReducer"].caseDetailData;
    const caseData = getState()["caseDataReducer"].caseData;
    const isLineCellClicked = getState()["caseDataReducer"].isLineCellClicked;

    const gridInstance = cellData.component;
    const codeValue = cellData.data?.cptHcpcs;
    const caseLineId = cellData.data?.caseLineId;
    const revenueCode = cellData.data?.revenueCode;
    const originalPaymentAmt = cellData.data?.originalPaymentAmtDecimal;
    const modifiers = [
      cellData.data?.procedureModifier1,
      cellData.data?.procedureModifier2,
      cellData.data?.procedureModifier3,
      cellData.data?.procedureModifier4,
    ].filter(Boolean);

    const buildObj = (code, type) => ({
      codeValue: code,
      targetType: type,
      caseDetailId: caseDetailData?.caseDetailId,
      type: "CLAIM",
      revenueCode,
      targetId: caseLineId,
      atRiskAmnt: originalPaymentAmt,
      reviewRemainingAmnt: originalPaymentAmt,
    });

    const onLineCheckboxChanged = (e) => {
      let list = targetTypeSelectedList || [];
      if (isCheckBoxVisibleForCharges && !e.value) {
        const existingIndex = list.findIndex(
          (item) =>
            item.targetId === caseLineId &&
            item.caseDetailId === caseDetailData?.caseDetailId &&
            item.type === "CLAIM"
        );
        if (existingIndex !== -1) {
          toggleConfirmationPopupVisible(true, {
            codeValue: "",
            targetType: "",
            caseDetailId: caseDetailData?.caseDetailId,
            type: "CLAIM",
            revenueCode,
            targetId: caseLineId,
            gridName: GRIDNAME.LINE,
          });
        }
        return;
      }
      else if(isCheckBoxVisibleForCharges && e.value){
        return;
      }
      if (!isLineCellClicked) {
        if (e.value) {
          if (codeValue) {
            list = list.filter(
              (item) =>
                !(
                  item.targetId === caseLineId &&
                  item.targetType === "CPTHCPCS" &&
                  item.codeValue === codeValue
                )
            );
            list.push({
              ...buildObj(codeValue, "CPTHCPCS"),
              isChecked: true,
            });
          } else if (modifiers.length > 0) {
            modifiers.forEach((mod) => {
              list = list.filter(
                (item) =>
                  !(
                    item.targetId === caseLineId &&
                    item.targetType === "MODIFIER" &&
                    item.codeValue === mod
                  )
              );
              list.push({ ...buildObj(mod, "MODIFIER"), isChecked: true });
            });
          }
        } else {
          // Add all CPT/HCPCS and modifiers for this caseLineId as unchecked (isChecked: false)
          if (codeValue) {
            list = list.filter(
              (item) =>
                !(
                  item.targetId === caseLineId &&
                  item.targetType === "CPTHCPCS" &&
                  item.codeValue === codeValue
                )
            );
            list.push({
              ...buildObj(codeValue, "CPTHCPCS"),
              isChecked: false,
            });
          }
          modifiers.forEach((mod) => {
            list = list.filter(
              (item) =>
                !(
                  item.targetId === caseLineId &&
                  item.targetType === "MODIFIER" &&
                  item.codeValue === mod
                )
            );
            list.push({ ...buildObj(mod, "MODIFIER"), isChecked: false });
          });
        }

        dispatch({
          type: SET_TARGET_TYPE_VALUE_LIST,
          data: { targetTypeSelectedList: list },
        });
        gridInstance.refresh();
      } else {
        dispatch({
          type: SET_LINE_CELL_CLICKED,
          data: { isLineCellClicked: false },
        });
      }
    };
    const findInTargetTypeSelectedList = (targetTypeSelectedList || []).some(
      (item) => item.targetId === caseLineId && item.isChecked === true
    );
    const allRoundCharges = getAllRoundCharges(caseData);
    let checkedDiagnosisCheckbox = false;
    if (!findInTargetTypeSelectedList) {
      let isTargetTypeValueUnchecked = (targetTypeSelectedList || []).some(
        (item) => item.targetId === caseLineId && item.isChecked === false
      );
      if (isTargetTypeValueUnchecked) {
        checkedDiagnosisCheckbox = false;
      } else {
        const findInAllRoundCharges =
          Array.isArray(allRoundCharges) &&
          allRoundCharges.some((item) => item.targetId === caseLineId);
        if (findInAllRoundCharges) {
          checkedDiagnosisCheckbox = true;
        } else {
          checkedDiagnosisCheckbox = false;
        }
      }
    } else {
      checkedDiagnosisCheckbox = findInTargetTypeSelectedList;
    }
    let isDisabled = isCheckBoxVisibleForCharges && !checkedDiagnosisCheckbox;
    return (
      <MdaeCheckBox
        id={"chkLines_" + cellData.rowIndex}
        onValueChanged={onLineCheckboxChanged}
        value={checkedDiagnosisCheckbox}
        disabled={isDisabled}
      />
    );
  };

    const deleteRecordsFromRoundCharges = (currentTargetIdSelected) => (
      dispatch,
      getState
    ) => {
      const targetTypeSelectedList = getState()["caseDataReducer"]
        .targetTypeSelectedList;
      const caseData = getState()["caseDataReducer"].caseData;
      let updatedList = (targetTypeSelectedList || []).filter(
        (item) =>
          !(
            item.targetId === currentTargetIdSelected.targetId &&
            item.caseDetailId === currentTargetIdSelected?.caseDetailId &&
            item.type === currentTargetIdSelected.type &&
            (currentTargetIdSelected.gridName === GRIDNAME.DIAGNOSIS
              ? item.codeValue === currentTargetIdSelected.codeValue &&
                item.targetType === currentTargetIdSelected.targetType
              : item.targetType === "CPTHCPCS" ||
                item.targetType === "MODIFIER")
          )
      );
      dispatch({
        type: SET_TARGET_TYPE_VALUE_LIST,
        data: { targetTypeSelectedList: updatedList },
      });
      const dataGrid = dxDataGrid.getInstance(
        document.getElementById("grdLines")
      ) as dxDataGrid;
      dataGrid.refresh();
      const updatedRoundData = caseData.roundData.map((round) => {
        const filteredCharges = round.roundCharges.filter(
          (charge) =>
            !(
              charge.targetId === currentTargetIdSelected.targetId &&
              charge.billingCaseDetailId ===
                currentTargetIdSelected?.caseDetailId &&
              charge.claimRemitType === currentTargetIdSelected.type &&
              (currentTargetIdSelected.gridName === GRIDNAME.DIAGNOSIS
                ? charge.roundTargetValue ===
                    currentTargetIdSelected.codeValue &&
                  charge.roundTargetType === currentTargetIdSelected.targetType
                : charge.roundTargetType === "CPTHCPCS" ||
                  charge.roundTargetType === "MODIFIER")
            )
        );
        return {
          ...round,
          roundCharges: [...filteredCharges],
        };
      });
      dispatch({
        type: SET_CASE_FORM_DATA,
        data: {
          caseData: {
            ...caseData,
            roundData: updatedRoundData,
          },
        },
      });
    };


const CaseFields: React.FC<ICaseFields> = (props) => {
  const dispatch = useDispatch();
  const completeCaseDetail = useSelector(
    (state) => state["caseDetailReducer"].completeCaseDetail,
    isEqual
  );
  const caseFieldsTabFlags = useSelector(
    (state) => state["caseFieldsReducer"].caseFieldsTabFlags,
    isEqual
  );
  const aliasBillDataFields = useSelector(
    (state: any) => state["caseDetailReducer"].aliasBillDataFields,
    isEqual
  );

  const isCaseSummaryDrawerVisible = useSelector(
    (state) => state["caseFieldsReducer"].isCaseSummaryDrawerVisible,
    isEqual
  );
  const isLineDataGridShowAllField = useSelector(
    (state) => state["caseFieldsReducer"].isLineDataGridShowAllField,
    isEqual
  );
  const caseDetailData = useSelector(
    (state) => state["caseDetailReducer"].caseDetailData,
    isEqual
  );
  const targetTypeSelectedList=useSelector(
    (state) => state["caseDataReducer"].targetTypeSelectedList
  );
  const caseDetailsDenialResponseList = useSelector(
    (state: any) => state.denialsPredictorReducer.caseDetailsDenialResponseList,
    checkListIsEqual
  );
  const responseForCaseLine = useSelector(
    (state: any) => state.denialsPredictorReducer.responseForCaseLine,
    isEqual
  );
   const caseData = useSelector(
      (state) => state["caseDataReducer"].caseData
  );
  const currentImportRoundData = useSelector(
    (state) => state["caseDataReducer"].currentImportRoundData
  );

  const isVisibleFromImportCharges = useSelector(
    (state) => state["billHistoryReducer"].isVisibleFromImportCharges
  );

  const auditPropertiesData = useSelector(
    (state) => state["auditPropertiesReducer"].auditPropertiesData,
    isEqual
  );

  const toggleConfirmationPopupVisible = (
    isVisible,
    currentTargetIdSelected
  ) => {
    if (isVisible) {
      showConfirmationPopup(currentTargetIdSelected);
    } else {
      hideConfirmationPopup();
    }
  };

  const hideConfirmationPopup = () => {
    dispatch({
      type: TOGGLE_CONFIRMATION_POPUP,
      data: {
        visible: false,
      },
    });
  };
  const showConfirmationPopup = (currentTargetIdSelected) => {
    dispatch({
      type: TOGGLE_CONFIRMATION_POPUP,
      data: {
        onConfirm: () => {
          hideConfirmationPopup();
          dispatch(deleteRecordsFromRoundCharges(currentTargetIdSelected));
        },
        visible: true,
        confirmShown: true,
        declineShown: true,
        cancelShown: false,
        width: 380,
        height: 200,
        contentHtml: `This line is currently being tracked via Round. These changes will delete any target types being tracked. Do you wish to proceed?`,
        title: "Remove Line Level",
        confirmLabel: "OK",
        declineLabel: "Cancel",
        onDecline: () => hideConfirmationPopup(),
      },
    });
  };

    useEffect(() => {
      const allRoundCharges = getAllRoundCharges(caseData);
      const list = allRoundCharges
        ?.filter(
          (rc) =>
            rc.claimRemitType === "CLAIM" &&
            rc.billingCaseDetailId === caseDetailData?.caseDetailId
        )
        .map((rc) => ({
          codeValue: rc.roundTargetValue,
          targetType: rc.roundTargetType,
          caseDetailId: rc.billingCaseDetailId,
          type: rc.claimRemitType,
          revenueCode: rc.revenueCode,
          targetId: rc.targetId,
          isChecked: true,
          atRiskAmnt: rc.atRiskAmnt,
        }));
      dispatch({
        type: SET_TARGET_TYPE_VALUE_LIST,
        data: { targetTypeSelectedList: list },
      });
    }, [dispatch]);

    useEffect(() => {
      if (!props.isDenialPredictorFlagAvailable || !completeCaseDetail.auditCaseLines) return;

      completeCaseDetail.auditCaseLines.map(function(lineItem) {
        var index = caseDetailsDenialResponseList.findIndex(function(item) {
          return lineItem.caseLineId == item.caseLineId;
        });
        lineItem.denialFlagInd = index != -1 ? 1 : 0;
      });
      var denialsFlags = completeCaseDetail.auditCaseLines.filter(function(item) {
        return item.denialFlagInd;
      });
      if (denialsFlags) {
        completeCaseDetail.auditCaseLines.sort(function(a, b) {
          return b.denialFlagInd - a.denialFlagInd;
        });
      }
      dispatch({
        type: SET_AUDIT_CASE_LINES,
        data: { auditCaseLines: completeCaseDetail.auditCaseLines },
      });
    }, [completeCaseDetail.auditCaseLines,props.isDenialPredictorFlagAvailable, caseDetailsDenialResponseList]);

  const isTargetValueChecked = useCallback(
    (obj, targetTypeSelectedList, allRoundCharges) => {
      let list = targetTypeSelectedList || [];
      const currentRowIndex = list.findIndex(
        (item) =>
          item.codeValue === obj.codeValue &&
          item.targetType === obj.targetType &&
          item.caseDetailId === obj.caseDetailId &&
          item.type === obj.type &&
          item.revenueCode === obj.revenueCode &&
          item.targetId === obj.targetId
      );
      if (currentRowIndex !== -1) {
        return list[currentRowIndex].isChecked;
      } else {
        return (
          Array.isArray(allRoundCharges) &&
          allRoundCharges.some(
            (item) =>
              item.roundTargetType === obj.targetType &&
              item.roundTargetValue === obj.codeValue &&
              item.claimRemitType === obj.type &&
              item.billingCaseDetailId === obj.caseDetailId &&
              item.targetId === obj.targetId
          )
        );
      }
    },
    []
  );

  const diagnosisCellPrepared = useCallback(
    (e) => {
      if (isVisibleFromImportCharges || props.isCheckBoxVisibleForCharges) {
        if (e.rowType === "data" && e.column.dataField === "codeValue") {
          let obj = {
            codeValue: e.data?.codeValue,
            targetType: "ICDD",
            caseDetailId: caseDetailData?.caseDetailId,
            type: "CLAIM",
            revenueCode: "",
            targetId: 0,
          };
          const checked = isTargetValueChecked(
            obj,
            targetTypeSelectedList,
            getAllRoundCharges(caseData)
          );
          e.cellElement.style.backgroundColor = checked ? "#15e846ff" : "";
        }
      }
    },
    [caseDetailData, targetTypeSelectedList, getAllRoundCharges,isVisibleFromImportCharges,props.isCheckBoxVisibleForCharges]
  );

  const diagnosisCheckboxCellRender = useCallback(
    (cellData) => {
      const gridInstance = cellData.component;
      const codeValue = cellData.data?.codeValue;
      const obj = {
        codeValue,
        targetType: "ICDD",
        caseDetailId: caseDetailData?.caseDetailId,
        type: "CLAIM",
        revenueCode: "",
        targetId: 0,
      };

      const handleValueChanged = (e) => {
        let list = targetTypeSelectedList || [];
        const existingIndex = list.findIndex(
          (item) =>
            item.codeValue === obj.codeValue &&
            item.targetType === obj.targetType &&
            item.caseDetailId === obj.caseDetailId &&
            item.type === obj.type
        );
         if (props.isCheckBoxVisibleForCharges && !e.value) {
           if (existingIndex !== -1) {
             toggleConfirmationPopupVisible(true, {
               codeValue,
               targetType: "ICDD",
               caseDetailId: caseDetailData?.caseDetailId,
               type: "CLAIM",
               revenueCode: "",
               targetId: 0,
               gridName: GRIDNAME.DIAGNOSIS,
             });
           }
           return;
         } else if (props.isCheckBoxVisibleForCharges && e.value) {
           return;
         }
        if (existingIndex !== -1) {
          //  Update existing
          list = [...list];
          list[existingIndex] = {
            ...list[existingIndex],
            ...obj,
            isChecked: e.value,
          };
        } else {
          //  Add new
          list = [...list, { ...obj, isChecked: e.value }];
        }
        dispatch({
          type: SET_TARGET_TYPE_VALUE_LIST,
          data: { targetTypeSelectedList: list },
        });
         gridInstance.refresh();
      };

      let allRoundCharges = getAllRoundCharges(caseData);
      const checked = isTargetValueChecked(
        obj,
        targetTypeSelectedList,
        allRoundCharges
      );

      const isDisabled = props.isCheckBoxVisibleForCharges && !checked ;
      return (
        <MdaeCheckBox
          id={"chkDiagnosis_" + codeValue}
          onValueChanged={handleValueChanged}
          value={checked}
          disabled={isDisabled}
        />
      );
    },
    [
      dispatch,
      targetTypeSelectedList,
      caseDetailData,
      caseData,
      currentImportRoundData,
      getAllRoundCharges,
      props.isCheckBoxVisibleForCharges,
      toggleConfirmationPopupVisible,
    ]
  );

  const icdProcedureCellPrepared = useCallback(
    (e) => {
      if (isVisibleFromImportCharges || props.isCheckBoxVisibleForCharges) {
        if (e.rowType === "data" && e.column.dataField === "codeValue") {
          let obj = {
            codeValue: e.data?.codeValue,
            targetType: "ICDP",
            caseDetailId: caseDetailData?.caseDetailId,
            type: "CLAIM",
            revenueCode: "",
            targetId: 0,
          };
          const checked = isTargetValueChecked(
            obj,
            targetTypeSelectedList,
            getAllRoundCharges(caseData)
          );
          e.cellElement.style.backgroundColor = checked ? "#15e846ff" : "";
        }
      }
    },
    [caseDetailData, targetTypeSelectedList, getAllRoundCharges, isVisibleFromImportCharges, props.isCheckBoxVisibleForCharges]
  );

  const icdProcedureCheckboxCellRender = useCallback(
    (cellData) => {
      const gridInstance = cellData.component;
      const codeValue = cellData.data?.codeValue;
      const obj = {
        codeValue,
        targetType: "ICDP",
        caseDetailId: caseDetailData?.caseDetailId,
        type: "CLAIM",
        revenueCode: "",
        targetId: 0,
      };

      const handleValueChanged = (e) => {
        let list = targetTypeSelectedList || [];
        const existingIndex = list.findIndex(
          (item) =>
            item.codeValue === obj.codeValue &&
            item.targetType === obj.targetType &&
            item.caseDetailId === obj.caseDetailId &&
            item.type === obj.type
        );
         if (props.isCheckBoxVisibleForCharges && !e.value) {
           if (existingIndex !== -1) {
             toggleConfirmationPopupVisible(true, {
               codeValue,
               targetType: "ICDP",
               caseDetailId: caseDetailData?.caseDetailId,
               type: "CLAIM",
               revenueCode: "",
               targetId: 0,
               gridName: GRIDNAME.DIAGNOSIS,
             });
           }
           return;
         } else if (props.isCheckBoxVisibleForCharges && e.value) {
           return;
         }
        if (existingIndex !== -1) {
          list = [...list];
          list[existingIndex] = {
            ...list[existingIndex],
            ...obj,
            isChecked: e.value,
          };
        } else {
          list = [...list, { ...obj, isChecked: e.value }];
        }
        dispatch({
          type: SET_TARGET_TYPE_VALUE_LIST,
          data: { targetTypeSelectedList: list },
        });
         gridInstance.refresh();
      };

      let allRoundCharges = getAllRoundCharges(caseData);
      const checked = isTargetValueChecked(
        obj,
        targetTypeSelectedList,
        allRoundCharges
      );

      const isDisabled = props.isCheckBoxVisibleForCharges && !checked ;
      return (
        <MdaeCheckBox
          id={"chkIcdProcedure_" + codeValue}
          onValueChanged={handleValueChanged}
          value={checked}
          disabled={isDisabled}
        />
      );
    },
    [
      dispatch,
      targetTypeSelectedList,
      caseDetailData,
      caseData,
      currentImportRoundData,
      getAllRoundCharges,
      props.isCheckBoxVisibleForCharges,
      toggleConfirmationPopupVisible,
    ]
  );

  const drgTargetType = completeCaseDetail.isAPRDRG ? "APR_DRG" : "DRG";

  const drgCheckboxValue = useMemo(() => {
    const obj = {
      codeValue: completeCaseDetail.drgDiagCode,
      targetType: drgTargetType,
      caseDetailId: caseDetailData?.caseDetailId,
      type: "CLAIM",
      revenueCode: "",
      targetId: 0,
    };
    return isTargetValueChecked(obj, targetTypeSelectedList, getAllRoundCharges(caseData));
  }, [completeCaseDetail.drgDiagCode, drgTargetType, caseDetailData, targetTypeSelectedList, caseData, isTargetValueChecked, getAllRoundCharges]);

  const onDrgCheckboxValueChanged = useCallback(
    (e) => {
      const obj = {
        codeValue: completeCaseDetail.drgDiagCode,
        targetType: drgTargetType,
        caseDetailId: caseDetailData?.caseDetailId,
        type: "CLAIM",
        revenueCode: "",
        targetId: 0,
      };
      let list = targetTypeSelectedList || [];
      const existingIndex = list.findIndex(
        (item) =>
          item.codeValue === obj.codeValue &&
          item.targetType === obj.targetType &&
          item.caseDetailId === obj.caseDetailId &&
          item.type === obj.type
      );
       if (props.isCheckBoxVisibleForCharges && !e.value) {
         if (existingIndex !== -1) {
           toggleConfirmationPopupVisible(true, {
             codeValue: completeCaseDetail.drgDiagCode,
             targetType: drgTargetType,
             caseDetailId: caseDetailData?.caseDetailId,
             type: "CLAIM",
             revenueCode: "",
             targetId: 0,
             gridName: GRIDNAME.DIAGNOSIS,
           });
         }
         return;
       } else if (props.isCheckBoxVisibleForCharges && e.value) {
         return;
       }
      if (existingIndex !== -1) {
        list = [...list];
        list[existingIndex] = {
          ...list[existingIndex],
          ...obj,
          isChecked: e.value,
        };
      } else {
        list = [...list, { ...obj, isChecked: e.value }];
      }
      dispatch({
        type: SET_TARGET_TYPE_VALUE_LIST,
        data: { targetTypeSelectedList: list },
      });
    },
    [
      dispatch,
      targetTypeSelectedList,
      caseDetailData,
      completeCaseDetail.drgDiagCode,
      drgTargetType,
      props.isCheckBoxVisibleForCharges,
      toggleConfirmationPopupVisible,
    ]
  );

  const {diagnosisDatagridConfigs, diagnosisCurrentConfigs} = useMemo(
    () =>
      getDiagnosisDatagridConfigs( caseDetailData, completeCaseDetail,diagnosisCheckboxCellRender,diagnosisCellPrepared,isVisibleFromImportCharges, props.isCheckBoxVisibleForCharges),
    [caseDetailData, completeCaseDetail, diagnosisCheckboxCellRender, diagnosisCellPrepared, isVisibleFromImportCharges, props.isCheckBoxVisibleForCharges]
  );

  useEffect(() => {
  if (!diagnosisCurrentConfigs) return;
  // Set _ClaimType
  if (diagnosisCurrentConfigs._ClaimType) {
    dispatch({
      type: SET_CASE_FIELDS_TAB_FLAGS,
      data: { _ClaimType: diagnosisCurrentConfigs._ClaimType },
    });
  }
  // Set patientTypeName if present
  if (diagnosisCurrentConfigs.patientTypeName) {
    dispatch({
      type: SET_CASE_FIELDS_TAB_FLAGS,
      data: { patientTypeName: diagnosisCurrentConfigs.patientTypeName },
    });
  }
  // Set tab flags for claimType
  switch (diagnosisCurrentConfigs.claimType?.toUpperCase()) {
    case "INPATIENT":
      dispatch({
        type: SET_CASE_FIELDS_TAB_FLAGS,
        data: { ...initialCaseFieldsTabFlags, isInPatient: true },
      });
      break;
    case "OUTPATIENT":
      dispatch({
        type: SET_CASE_FIELDS_TAB_FLAGS,
        data: { ...initialCaseFieldsTabFlags, isOutPatient: true },
      });
      break;
    case "PROFESSIONAL":
      dispatch({
        type: SET_CASE_FIELDS_TAB_FLAGS,
        data: { ...initialCaseFieldsTabFlags, isProfessional: true },
      });
      break;
    default:
      break;
  }
  }, [
    diagnosisCurrentConfigs?._ClaimType,
    diagnosisCurrentConfigs?.patientTypeName,
    diagnosisCurrentConfigs?.claimType,
    dispatch,
  ]);

  const externalCauseOfInjurysDatagridConfigs = useMemo(
    () => getExternalCauseOfInjuryDatagridProps(completeCaseDetail),
    [completeCaseDetail]
  );
  const visitReasonDatagridProps = useMemo(
    () => getVisitReasonDatagridProps(completeCaseDetail),
    [completeCaseDetail]
  );
  const iCDProceduresDatagridProps = useMemo(
    () => getICDProceduresDatagridProps(completeCaseDetail, icdProcedureCheckboxCellRender, icdProcedureCellPrepared, isVisibleFromImportCharges, props.isCheckBoxVisibleForCharges),
    [completeCaseDetail, icdProcedureCheckboxCellRender, icdProcedureCellPrepared, isVisibleFromImportCharges, props.isCheckBoxVisibleForCharges]
  );
  const lineLevelDenialPredictorPopoverTarget = useSelector(
    (state: any) =>
      state.denialsPredictorReducer.lineLevelDenialPredictorPopoverTarget,
    isEqual
  );
  const isLineLevelDenialPredictorPopoverVisible = useSelector(
    (state: any) =>
      state.denialsPredictorReducer.isLineLevelDenialPredictorPopoverVisible,
    isEqual
  );
  const isShowChkbxViewCmpnt = useSelector(
    (state) => state["caseListReducer"].isShowChkbxViewCmpnt
  );
  const targetTypeSelectedListRef = useRef(targetTypeSelectedList);

  useEffect(() => {
    targetTypeSelectedListRef.current = targetTypeSelectedList;
  }, [targetTypeSelectedList]);

  const onLineCellPrepared = useCallback(
    (e) => {
      if (
        e.rowType === "data" &&
        e.column.dataField === "caseLineId" &&
        !caseDetailsDenialResponseList?.some(
          (item) => item.caseLineId == e.data.caseLineId
        )
      ) {
        e.cellElement.children[0]?.remove();
      }
      if (
        e.rowType === "data" &&
        e.column.dataField === "caseLineId" &&
        caseDetailsDenialResponseList?.some(
          (item) => item.caseLineId == e.data.caseLineId
        )
      ) {
        var responseForCaseLine = caseDetailsDenialResponseList?.filter(
          (x) => x.caseLineId == e.data.caseLineId
        );
        const caseLineId = e.data.caseLineId;
        if (responseForCaseLine.length > 1) {
          e.cellElement.addEventListener("mouseenter", (e) => {
            dispatch({
              type: TOGGLE_LINE_LEVEL_DENIAL_PREDICTOR_POPOVER,
              data: {
                lineLevelDenialPredictorPopoverTarget:
                  ".case-line-assigned-to-no" + caseLineId,
                isLineLevelDenialPredictorPopoverVisible: true,
                responseForCaseLine: responseForCaseLine,
              },
            });
          });
          e.cellElement.addEventListener("mouseleave", (e) => {
            dispatch({
              type: TOGGLE_LINE_LEVEL_DENIAL_PREDICTOR_POPOVER,
              data: {
                lineLevelDenialPredictorPopoverTarget:
                  ".case-line-assigned-to-no" + caseLineId,
                isLineLevelDenialPredictorPopoverVisible: false,
              },
            });
          });
        } else {
          e.cellElement.addEventListener("mouseenter", (e) => {
            dispatch({
              type: TOGGLE_LINE_LEVEL_DENIAL_PREDICTOR_POPOVER,
              data: {
                lineLevelDenialPredictorPopoverTarget:
                  ".case-line-assigned-to-no" + caseLineId,
                isLineLevelDenialPredictorPopoverVisible: true,
                responseForCaseLine: responseForCaseLine,
              },
            });
          });
          e.cellElement.addEventListener("mouseleave", (e) => {
            dispatch({
              type: TOGGLE_LINE_LEVEL_DENIAL_PREDICTOR_POPOVER,
              data: {
                lineLevelDenialPredictorPopoverTarget:
                  ".case-line-assigned-to-no" + caseLineId,
                isLineLevelDenialPredictorPopoverVisible: false,
              },
            });
          });
        }
      }
      if(isVisibleFromImportCharges || props.isCheckBoxVisibleForCharges){
      if (e.rowType === "data") {
        if (e.column.dataField === "cptHcpcs") {
          let obj = {
            codeValue: e.data?.cptHcpcs,
            targetType: "CPTHCPCS",
            caseDetailId: caseDetailData?.caseDetailId,
            type: "CLAIM",
            revenueCode: e.data?.revenueCode,
            targetId: e.data?.caseLineId,
          };
          const checked = isTargetValueChecked(
            obj,
            targetTypeSelectedListRef.current,
            getAllRoundCharges(caseData)
          );

          e.cellElement.style.backgroundColor = checked ? "#15e846ff" : "";
        }
        // Modifier highlight
        [
          "procedureModifier1",
          "procedureModifier2",
          "procedureModifier3",
          "procedureModifier4",
        ].forEach((modField) => {
          if (e.column.dataField === modField && e.data[modField]) {
            let obj = {
              codeValue: e.data[modField],
              targetType: "MODIFIER",
              caseDetailId: caseDetailData?.caseDetailId,
              type: "CLAIM",
              revenueCode: e.data?.revenueCode,
              targetId: e.data?.caseLineId,
            };
            const checked = isTargetValueChecked(
              obj,
              targetTypeSelectedListRef.current,
              getAllRoundCharges(caseData)
            );

            e.cellElement.style.backgroundColor = checked ? "#15e846ff" : "";
          }
        });
      }
    }
    },
    [
      caseDetailData,
      caseDetailsDenialResponseList,
      getAllRoundCharges,
      isTargetValueChecked,
      dispatch,
      isVisibleFromImportCharges,
      props.isCheckBoxVisibleForCharges,
    ]
  );



  const onLineCellClick = useCallback(
    (e) => {
      if (
        e.rowType === "data" &&
        e.column.dataField === "caseLineId" &&
        caseDetailsDenialResponseList.some(
          (item) => item.caseLineId == e.data.caseLineId
        )
      ) {
        var updatedCaseDetailsDenialResponseList = caseDetailsDenialResponseList.filter(
          (x) => x.caseLineId == e.data.caseLineId
        );
        for (var i = 0; i <= caseDetailsDenialResponseList.length - 1; i++) {
          if (
            caseDetailsDenialResponseList[i].caseLineId != e.data.caseLineId
          ) {
            updatedCaseDetailsDenialResponseList.push(
              caseDetailsDenialResponseList[i]
            );
          }
        }
        dispatch({
          type: SET_RESPONSE_ALERT_DATA,
          data: {
            caseDetailsDenialResponseList: assignVersionToList(
              updatedCaseDetailsDenialResponseList
            ),
          },
        });
        toggleDenialPredictorResponseGridPopup(
          dispatch,
          true,
          e.data.caseLineId
        );
      }
      if(isVisibleFromImportCharges){
      if (e.rowType === "data") {
        const gridInstance = e.component;

        const codeValue = e.data?.cptHcpcs;
        const caseLineId = e.data?.caseLineId;
        const revenueCode = e.data?.revenueCode;
        const originalPaymentAmt = e.data?.originalPaymentAmtDecimal;
        const modifiers = [
          e.data?.procedureModifier1,
          e.data?.procedureModifier2,
          e.data?.procedureModifier3,
          e.data?.procedureModifier4,
        ].filter(Boolean);

        // Helper to build selection objects
        const buildObj = (code, type) => ({
          codeValue: code,
          targetType: type,
          caseDetailId: caseDetailData?.caseDetailId,
          type: "CLAIM",
          revenueCode,
          targetId: caseLineId,
          atRiskAmnt: originalPaymentAmt,
          reviewRemainingAmnt: originalPaymentAmt,
        });

        let list = targetTypeSelectedList || [];
        let changed = false;
        // CPT/HCPCS cell click
        if (e.column.dataField === "cptHcpcs") {
          const idx = list.findIndex(
            (item) =>
              item.codeValue === codeValue &&
              item.targetType === "CPTHCPCS" &&
              item.targetId === caseLineId
          );

          if (list[idx]?.isChecked) {
            // Remove CPT/HCPCS and add all modifiers if present
            list = list.filter(
              (item) =>
                !(
                  item.codeValue === codeValue &&
                  item.targetType === "CPTHCPCS" &&
                  item.targetId === caseLineId
                )
            );
            list.push({ ...buildObj(codeValue, "CPTHCPCS"), isChecked: false });
          } else if (idx !== -1 && !list[idx]?.isChecked) {
            list = list.filter(
              (item) =>
                !(
                  item.codeValue === codeValue &&
                  item.targetType === "CPTHCPCS" &&
                  item.targetId === caseLineId
                )
            );
            list.push({ ...buildObj(codeValue, "CPTHCPCS"), isChecked: true });
          } else {
            list.push({ ...buildObj(codeValue, "CPTHCPCS"), isChecked: true });
          }
          changed = true;
        }
        [
          "procedureModifier1",
          "procedureModifier2",
          "procedureModifier3",
          "procedureModifier4",
        ].forEach((modField) => {
          if (e.column.dataField === modField && e.data[modField]) {
            const mod = e.data[modField];
            const idx = list.findIndex(
              (item) =>
                item.codeValue === mod &&
                item.targetType === "MODIFIER" &&
                item.targetId === caseLineId
            );
            if (list[idx]?.isChecked) {
              list = list.filter(
                (item) =>
                  !(
                    item.codeValue === mod &&
                    item.targetType === "MODIFIER" &&
                    item.targetId === caseLineId
                  )
              );
              list.push({ ...buildObj(mod, "MODIFIER"), isChecked: false });
            } else if (idx !== -1 && !list[idx]?.isChecked) {
              list = list.filter(
                (item) =>
                  !(
                    item.codeValue === mod &&
                    item.targetType === "MODIFIER" &&
                    item.targetId === caseLineId
                  )
              );
              list.push({ ...buildObj(mod, "MODIFIER"), isChecked: true });
            } else {
              list.push({ ...buildObj(mod, "MODIFIER"), isChecked: true });
            }
            changed = true;
          }
        });

        if (changed) {
          dispatch({
            type: SET_TARGET_TYPE_VALUE_LIST,
            data: { targetTypeSelectedList: list },
          });
          dispatch({
            type: SET_LINE_CELL_CLICKED,
            data: { isLineCellClicked: true },
          });
          gridInstance.refresh();
        }
      }
    }
    },
    [
      targetTypeSelectedList,
      caseDetailsDenialResponseList,
      dispatch,
      caseDetailData,
      isVisibleFromImportCharges
    ]
  );

  const showCompositeRevenuecodes = useCallback(
    (caseLineId) =>(dispatch,getState)=> {
      if (caseLineId === 0) return;

    const oldDetail  = getState()["caseDetailReducer"].completeCaseDetail;
    // Find the clicked item
    const objItem = oldDetail.auditCaseLinesWithComponentRevenueCodes
      .find((obj) => obj.caseLineId === caseLineId);

    if (!objItem) return;

    const expanded = !objItem.isComponentRevenueCodesExpanded;

    // NEW ARRAY: toggle only the target caseLineId and all its children
    const newAuditLinesWithComp = oldDetail.auditCaseLinesWithComponentRevenueCodes.map((obj) => {
      if (obj.caseLineId === caseLineId) {
        return { ...obj, isComponentRevenueCodesExpanded: expanded };
      }

      if (obj.parentCaseLineId === caseLineId) {
        return { ...obj, isComponentRevenueCodesExpanded: expanded };
      }

      return obj; // untouched
    });

    // NEW ARRAY: rebuild auditCaseLines
    const newAuditCaseLines = newAuditLinesWithComp.filter(
      (e) => e.parentCaseLineId === 0 || e.isComponentRevenueCodesExpanded === true
    );

    // NEW OBJECT: build new caseDetail
    const newCaseDetail = {
      ...oldDetail,
      auditCaseLinesWithComponentRevenueCodes: newAuditLinesWithComp,
      auditCaseLines: newAuditCaseLines,
    };

    // Dispatch immutable updated data
    dispatch({
      type: SET_COMPLETE_AUDIT_CASE_DETAIL,
      data: { completeCaseDetail: newCaseDetail },
    });

    const dataGrid = dxDataGrid.getInstance(
      document.getElementById("grdLines")
    ) as dxDataGrid;

    dataGrid.option("dataSource", newCaseDetail.auditCaseLines);
    },
    [completeCaseDetail, dispatch]
  );

  const cellLineCellTemplate = useCallback(
    (e) => {
      let isCompositeContainComponentRows = completeCaseDetail?.auditCaseLinesWithComponentRevenueCodes.some(
        (x) => x.parentCaseLineId == e.data.caseLineId
      );
      return (
        <div>
          {e.data?.parentCaseLineId == 0 && isCompositeContainComponentRows && (
            <div>
              <div
                onClick={() => dispatch(showCompositeRevenuecodes(e.data.caseLineId))}
                className="grid-buttons"
                style={{ padding: 0 }}
              >
                {!e.data.isComponentRevenueCodesExpanded ? (
                  <i className="fas glyphicon glyphicon-triangle-right mdae-datagrid-action-ellipsis-icon"></i>
                ) : (
                  <i className="fas glyphicon glyphicon-triangle-bottom mdae-datagrid-action-ellipsis-icon"></i>
                )}
              </div>
            </div>
          )}
        </div>
      );
    },
    [completeCaseDetail, showCompositeRevenuecodes]
  );

  const linesDatagridProps = useMemo(
    () =>
      getLinesDatagridProps(
        caseFieldsTabFlags,
        isLineDataGridShowAllField,
        props.isDenialPredictorFlagAvailable,
        cellLineCellTemplate,
        isShowChkbxViewCmpnt,
        dispatch,
        aliasBillDataFields,
        isVisibleFromImportCharges,
        props.isCheckBoxVisibleForCharges,
        getAllRoundCharges,
        toggleConfirmationPopupVisible,
        completeCaseDetail.isComponentRowsAvailable
      ),
    [
      completeCaseDetail.isComponentRowsAvailable,
      caseFieldsTabFlags,
      isLineDataGridShowAllField,
      props.isDenialPredictorFlagAvailable,
      isShowChkbxViewCmpnt,
      isVisibleFromImportCharges,
      props.isCheckBoxVisibleForCharges,
    ]
  );
  const payerDatagridProps = useMemo(
    () => getPayerDatagridProps(completeCaseDetail),
    [completeCaseDetail]
  );
  const occuranceDatagridProps = useMemo(
    () => getOccuranceDatagridProps(completeCaseDetail),
    [completeCaseDetail]
  );
  const conditionDatagridProps = useMemo(
    () => getConditionDatagridProps(completeCaseDetail),
    [completeCaseDetail]
  );
  const occuranceSpanDatagridProps = useMemo(
    () => getOccuranceSpanDatagridProps(completeCaseDetail),
    [completeCaseDetail]
  );
  const currentCptYear = new Date().getFullYear();

  return (
    <div>
      <LinkedAuditItems />
      <div className="case-detail-tab">
        <div
          className="row remove-margin"
          style={{
            marginBottom: "10px !important",
          }}
        >
          {/* Diagnoses grid */}
          <div
            className={
              caseFieldsTabFlags.isProfessional
                ? isCaseSummaryDrawerVisible
                  ? "col-xs-8"
                  : "col-xs-10"
                : isCaseSummaryDrawerVisible
                ? "col-xs-4"
                : "col-xs-6"
            }
          >
            <div className="row remove-margin">
              <div className="col-xs-12 remove-padding mdae-data-field-label">
                Diagnoses
              </div>
            </div>
            <div>
              <MdaeDataGrid id="grdDiagnoses" {...diagnosisDatagridConfigs} />
            </div>
          </div>
          {/* External cause grid */}
          <div
            className={
              caseFieldsTabFlags.isProfessional ? "hide-control" : "col-xs-4"
            }
          >
            <div
              className={
                caseFieldsTabFlags.isInPatient ||
                caseFieldsTabFlags.isOutPatient
                  ? "row remove-margin"
                  : "hide-control"
              }
            >
              <div className="col-xs-12 remove-padding">
                <div className="justify-items-space-between">
                  <div className="mdae-data-field-label">
                    External Causes of Injury
                  </div>
                  {/* Add button here */}
                </div>
                <MdaeDataGrid
                  id="gridExternalInjury"
                  className="bottom-margin"
                  {...externalCauseOfInjurysDatagridConfigs}
                />
              </div>
            </div>

            <div
              className={
                caseFieldsTabFlags.isOutPatient
                  ? "row remove-margin"
                  : "hide-control"
              }
              style={{ marginBottom: "10px !important" }}
            >
              <div className="col-xs-12 remove-padding">
                <div className="justify-items-space-between">
                  <div className="mdae-data-field-label">Visit Reason</div>
                  {/* Add button here */}
                </div>
                <MdaeDataGrid
                  id="grdVisitReason"
                  {...visitReasonDatagridProps}
                  className="bottom-margin"
                />
              </div>
            </div>

            {caseFieldsTabFlags.isInPatient && (
              <>
                {(isVisibleFromImportCharges || props.isCheckBoxVisibleForCharges) && completeCaseDetail.drgDiagCode ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ marginTop: '8px' }}>
                      <MdaeCheckBox
                        id="chkDrg"
                        value={drgCheckboxValue}
                        onValueChanged={onDrgCheckboxValueChanged}
                        disabled={props.isCheckBoxVisibleForCharges && !drgCheckboxValue}
                      />
                    </div>
                    <MdaeTextBox
                      id="txtDrgAuditType"
                      mdaeTextBoxTitle="DRG"
                      className="bottom-margin"
                      readOnly
                      mdaeDataUpdaterConfig={[
                        {
                          actionType: GET_CASE_CLAIM_DETAILS,
                          isAsyncComplete: true,
                          widgetOptionBindings: {
                            value: "drgDiagCode",
                          },
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <MdaeTextBox
                    id="txtDrgAuditType"
                    mdaeTextBoxTitle="DRG"
                    className="bottom-margin"
                    readOnly
                    mdaeDataUpdaterConfig={[
                      {
                        actionType: GET_CASE_CLAIM_DETAILS,
                        isAsyncComplete: true,
                        widgetOptionBindings: {
                          value: "drgDiagCode",
                        },
                      },
                    ]}
                  />
                )}
                <MdaeTextBox
                  id="txtAdmissionDiagCode"
                  mdaeTextBoxTitle="Admit Dx"
                  className="bottom-margin"
                  readOnly
                  mdaeDataUpdaterConfig={[
                    {
                      actionType: GET_CASE_CLAIM_DETAILS,
                      isAsyncComplete: true,
                      widgetOptionBindings: {
                        value: "admittingDiagCode",
                      },
                    },
                  ]}
                />
                <div className="row remove-margin">
                  <div className="col-xs-12 remove-padding">
                    <div className="justify-items-space-between">
                      <div className="mdae-data-field-label">
                        ICD Procedures
                      </div>
                      {/* Add button here */}
                    </div>
                    <MdaeDataGrid
                      id="grdICDProcedures"
                      className="bottom-margin"
                      {...iCDProceduresDatagridProps}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          {/* paynment text boxes and summary panel */}
          <div
            className={isCaseSummaryDrawerVisible ? "col-xs-4" : "col-xs-2"}
            style={{ marginTop: 10 }}
          >
            <div
              className={isCaseSummaryDrawerVisible ? "col-xs-5" : "col-xs-10"}
              style={{ paddingLeft: 0 }}
            >
              <MdaeNumericBox
                id="txtOriginalPayment"
                mdaeNumberBoxTitle="Original Payment"
                className="bottom-margin text-right"
                format="$ #,##0.##"
                value={parseFloat(completeCaseDetail.originalPaymentAmt)}
                showSpinButtons={false}
                readOnly
              />
              {!(props.isFromCasePreview || props.isFromExternalAudit) && (
                <>
                  <MdaeNumericBox
                    id="txtSupportedPayment"
                    mdaeNumberBoxTitle="Supported Payment"
                    className="bottom-margin text-right"
                    format="$ #,##0.##"
                    value={parseFloat(completeCaseDetail.supportedPaymentAmt)}
                    showSpinButtons={false}
                    readOnly
                  />
                  <MdaeTextBox
                    id="txtFinancualImpact"
                    mdaeTextBoxTitle="Financial Impact"
                    className="bottom-margin text-right"
                    value={
                      (completeCaseDetail.financialImpactAmtForView?.includes(
                        "$"
                      )
                        ? ""
                        : "$") +
                      (completeCaseDetail?.financialImpactAmtForView || "")
                    }
                    disabled
                  />
                  <MdaeNumericBox
                    id="txtOverPayent"
                    mdaeNumberBoxTitle="Over Payment"
                    className="bottom-margin text-right"
                    format="$ #,##0.##"
                    value={parseFloat(completeCaseDetail.overPaymentAmt)}
                    showSpinButtons={false}
                    disabled
                  />
                  <MdaeNumericBox
                    id="txtUnderPayment"
                    mdaeNumberBoxTitle="Under Payment"
                    className="bottom-margin text-right"
                    format="$ #,##0.##"
                    value={parseFloat(completeCaseDetail.underPaymentAmt)}
                    showSpinButtons={false}
                    disabled
                  />
                </>
              )}
            </div>
            <div
              className={isCaseSummaryDrawerVisible ? "col-xs-7" : "col-xs-2"}
            >
              <div
                className={
                  "application-section-primary-background-color show-summary-panel " +
                  (isCaseSummaryDrawerVisible ? "hide-control" : "")
                }
              >
                <MdaeButton
                  id="btnShowSummary"
                  role="dxButton"
                  icon="/images/ui-rewrite-icons/drawermenubackbuttonwhiteicon.svg"
                  stylingMode="text"
                  onClick={() => {
                    dispatch({
                      type: TOGGLE_CASE_SUMMARY_DRAWER,
                    });
                  }}
                />
                <div className="show-case-summary-lable">Show Summary</div>
              </div>

              <div
                id="pnlSummary"
                className={!isCaseSummaryDrawerVisible ? "hide-control" : ""}
              >
                <div className="application-section-primary-background-color hide-summary-panel">
                  <MdaeButton
                    id="btnHideSummary"
                    role="dxButton"
                    icon="/images/ui-rewrite-icons/drawermenubackbuttonwhiteicon.svg"
                    style={{ transform: "rotate(180deg)" }}
                    stylingMode="text"
                    onClick={() => {
                      dispatch({
                        type: TOGGLE_CASE_SUMMARY_DRAWER,
                      });
                    }}
                  />
                  <span className="hide-case-summary-lable">Hide Summary</span>
                </div>
                <MdaeScrollView>
                  <div className="row case-detail-content application-section-primary-color">
                    <>
                      <div className="col-xs-12">
                        {completeCaseDetail.patientLastName},{" "}
                        {completeCaseDetail.patientFirstName}
                      </div>
                      <div className="row detail-margin-top-bottom ">
                        <div className="col-xs-12  ">
                          MRN: {completeCaseDetail.patientMRN}
                        </div>
                      </div>
                      <div className="row detail-margin-top-bottom ">
                        <div className="col-xs-12">
                          ACCOUNT#:{completeCaseDetail.patientAcctNo}
                        </div>
                      </div>
                      <div className="row detail-margin-top-bottom ">
                        <div className="col-xs-12">&nbsp;</div>
                      </div>
                      {(caseFieldsTabFlags.isInPatient ||
                        caseFieldsTabFlags.isOutPatient) && (
                        <div className="row detail-margin-top-bottom ">
                          <div className="col-xs-12">
                            {completeCaseDetail.facilityName}
                          </div>
                        </div>
                      )}
                      <div className="row detail-margin-top-bottom ">
                        <div className="col-xs-12">
                          {caseFieldsTabFlags._ClaimType}
                          {caseFieldsTabFlags.patientTypeName}
                        </div>
                      </div>
                      {caseFieldsTabFlags.isInPatient || (
                        <div className="row detail-margin-top-bottom ">
                          <div className="col-xs-12">
                            {completeCaseDetail.statementFromDate} -
                            {completeCaseDetail.statementToDate}
                          </div>
                        </div>
                      )}
                      {/* Display Admit Date/Discharege Date for Inpatient Hospital */}
                      {caseFieldsTabFlags.isInPatient && (
                        <div className="row detail-margin-top-bottom ">
                          <div className="col-xs-12">
                            {completeCaseDetail.admissionDate} -
                            {completeCaseDetail.dischargeDate}
                          </div>
                        </div>
                      )}
                      {/* Newly Added INPATIENT */}
                      {caseFieldsTabFlags.isInPatient && (
                        <>
                          <div className="row detail-margin-top-bottom ">
                            <div className="col-xs-12">&nbsp;</div>
                          </div>
                          <div className="row detail-margin-top-bottom ">
                            <div className="col-xs-12">
                              {completeCaseDetail.drgDiagCode} -
                              {completeCaseDetail.drgDesc}
                            </div>
                          </div>
                        </>
                      )}
                      {/* Newly Added PROFESSIONAL */}
                      {caseFieldsTabFlags.isProfessional && (
                        <>
                          <div className="row detail-margin-top-bottom ">
                            <div className="col-xs-12">&nbsp;</div>
                          </div>
                          <div className="row detail-margin-top-bottom ">
                            <div className="col-xs-12">
                              Rendering Provider:{" "}
                              {completeCaseDetail.renderingProvider}
                            </div>
                          </div>
                        </>
                      )}
                      {/* Newly Added FOR ALL */}
                      <div className="row detail-margin-top-bottom ">
                        <div className="col-xs-12">
                          {completeCaseDetail.auditCaseParticipants?.map(
                            (payer) => (
                              <div className="row">
                                <div className="col-xs-12">
                                  {payer.payorResponsibilitySeqNo}.{" "}
                                  {payer.participantName}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </>
                  </div>
                </MdaeScrollView>
              </div>
            </div>
            {!props.isHideBillingHistoryLink && !isManualCase(caseDetailData) && (
              <div
                className={
                  isCaseSummaryDrawerVisible ? "col-xs-10" : "col-xs-12"
                }
                style={{ paddingLeft: 0 }}
              >
                <MdaeButton
                  role="dxButton"
                  id="pnlViewCaseBillingHistoryLink"
                  stylingMode="text"
                  onClick={() => {
                    if (caseDetailData?.newRecordInd === undefined) {
                      const data = {
                        parent: {
                          ...caseDetailData,
                          eaCaseId: caseDetailData.eaCaseId,
                          caseId: caseDetailData.caseId,
                          patientFullName:
                            completeCaseDetail.patientLastName +
                            ", " +
                            completeCaseDetail.patientFirstName,
                          fromServiceDate: completeCaseDetail.statementFromDate,
                          toServiceDate: completeCaseDetail.statementToDate,
                        },
                        child: {
                          caseBranchNo: caseDetailData.caseBranchNo,
                          sequence: caseDetailData.sequence,
                          billTypeFacility: caseDetailData.billTypeFacility,
                          totalCharges: caseDetailData.totalCharges,
                        },
                      };
                      toggleBillHistoryPopupVisible(dispatch, true, data);
                    }
                     else {
                      const billData = {
                        parent: caseDetailData,
                        child: first(caseDetailData.caseBillingHistoryList),
                      };
                      toggleBillHistoryPopupVisible(dispatch, true, billData);
                    }
                  }}
                >
                  <div className="view-billing-history-link col-xs-12">
                    View Billing History
                  </div>
                </MdaeButton>{" "}
              </div>
            )}
          </div>
        </div>
        {/* lines */}
        <div className="row remove-margin">
          <div className="col-xs-12">
            <div style={{marginTop: 10, marginBottom: 10}}>
              <ViewRevLineComponentCheckboxComponent
                  eaAuditId={auditPropertiesData?.eaAuditId}
                />
            </div>
            <div className="justify-items-space-between">
              <div className="mdae-data-field-label">Lines</div>
              {/* Add button here */}
            </div>
            <MdaeDataGrid
              id="grdLines"
              className="bottom-margin"
              onCellPrepared={onLineCellPrepared}
              onCellClick={onLineCellClick}
              dataSource={completeCaseDetail.auditCaseLines}
              height={() => getHeight(completeCaseDetail.auditCaseLines)}
              {...linesDatagridProps}
            />
            {responseForCaseLine?.length > 0 && (
              <MdaePopover
                id="pnlLineLevelDenialPredictorPopover"
                target={lineLevelDenialPredictorPopoverTarget}
                visible={isLineLevelDenialPredictorPopoverVisible}
              >
                <div>
                  {responseForCaseLine.length > 1 && (
                    <div>
                      Denials Predictor has flagged this line for multiple
                      rules.
                      <div>Click on this icon to see all flags on the case</div>
                    </div>
                  )}
                  {responseForCaseLine.length == 1 && (
                    <>
                      <div>
                        Denials Predictor has flagged this claim as a potential
                        denial risk:
                      </div>
                      <div className={"denial-popover-info"}>Line</div>
                      {responseForCaseLine[0].cptDrg}
                      {responseForCaseLine[0].claimTypeCode.toUpperCase() ==
                        "I" && (
                        <div>
                          <div className={"denial-popover-info"}>
                            Revenue Code
                          </div>
                          {responseForCaseLine[0].revenueCode}
                        </div>
                      )}
                      <div className={"denial-popover-info"}>Rule</div>
                      {responseForCaseLine[0].ruleDetail}
                      <div className={"denial-popover-info"}>Explanation</div>
                      {responseForCaseLine[0].ruleExplanation}
                      <div className={"denial-popover-info"}>
                        Recommendation
                      </div>
                      {responseForCaseLine[0].actionRecommendation}
                      <div className={"denial-popver-text"}>
                        Click on this warning to see more details
                      </div>
                    </>
                  )}
                </div>
              </MdaePopover>
            )}
          </div>
        </div>
        <div className="row  remove-margin">
          <div className="col-xs-12 text-right">
            <MdaeButton
              id="btnShowHideLineAllFieldsView"
              role="secondary"
              stylingMode="text"
              className="application-section-primary-color"
              onClick={() => {
                dispatch({ type: TOGGLE_LINE_DATAGRID_SHOW_ALL_FIELDS_VIEW });
              }}
            >
              {(isLineDataGridShowAllField ? "Hide" : "Show") +
                " all fields view"}
            </MdaeButton>
          </div>
        </div>
        {/* payer */}
        <div className="row remove-margin">
          <div className="col-xs-3">
            {/* IN PATIENT ADMIT DATE */}
            {caseFieldsTabFlags.isInPatient && (
              <>
                <div className="row">
                  <div className="col-xs-6">
                    <MdaeDateBox
                      id="dtAdmitDateValue"
                      mdaeDateBoxTitle="Admit Date"
                      className="bottom-margin"
                      readOnly
                      mdaeDataUpdaterConfig={[
                        {
                          actionType: GET_CASE_CLAIM_DETAILS,
                          isAsyncComplete: true,
                          widgetOptionBindings: {
                            value: "admissionDate",
                          },
                        },
                      ]}
                    />
                  </div>
                  <div className="col-xs-6">
                    <MdaeTextBox
                      id="txtAdmitHourValue"
                      mdaeTextBoxTitle="Admit Hr"
                      className="bottom-margin"
                      readOnly
                      mdaeDataUpdaterConfig={[
                        {
                          actionType: GET_CASE_CLAIM_DETAILS,
                          isAsyncComplete: true,
                          widgetOptionBindings: {
                            value: "admitHour",
                          },
                        },
                      ]}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-6">
                    <MdaeDateBox
                      id="dtDischargeDateValue"
                      mdaeDateBoxTitle="Discharge Date"
                      maxLength={2}
                      className="bottom-margin"
                      readOnly
                      mdaeDataUpdaterConfig={[
                        {
                          actionType: GET_CASE_CLAIM_DETAILS,
                          isAsyncComplete: true,
                          widgetOptionBindings: {
                            value: "dischargeDate",
                          },
                        },
                      ]}
                    />
                  </div>
                  <div className="col-xs-6">
                    <MdaeTextBox
                      id="txtDischargeHourValue"
                      mdaeTextBoxTitle="Discharge Hr"
                      className="bottom-margin"
                      readOnly
                      mdaeDataUpdaterConfig={[
                        {
                          actionType: GET_CASE_CLAIM_DETAILS,
                          isAsyncComplete: true,
                          widgetOptionBindings: {
                            value: "dischargeHour",
                          },
                        },
                      ]}
                    />
                  </div>
                </div>
              </>
            )}
            {/* OUT PATIENT ADMIT DATE */}
            {caseFieldsTabFlags.isInPatient || (
              <div className="row remove-margin">
                <div>
                  <MdaeDateBox
                    id="dtStatementFromDate"
                    mdaeDateBoxTitle="From Date"
                    className="bottom-margin"
                    readOnly
                    mdaeDataUpdaterConfig={[
                      {
                        actionType: GET_CASE_CLAIM_DETAILS,
                        isAsyncComplete: true,
                        widgetOptionBindings: {
                          value: "statementFromDate",
                        },
                      },
                    ]}
                  />
                </div>
                <div>
                  <MdaeDateBox
                    id="dtStatementToDate"
                    mdaeDateBoxTitle="To Date"
                    className="bottom-margin"
                    readOnly
                    mdaeDataUpdaterConfig={[
                      {
                        actionType: GET_CASE_CLAIM_DETAILS,
                        isAsyncComplete: true,
                        widgetOptionBindings: {
                          value: "statementToDate",
                        },
                      },
                    ]}
                  />
                </div>
              </div>
            )}
            {(caseFieldsTabFlags.isInPatient ||
              caseFieldsTabFlags.isOutPatient) && (
              <>
                <div className="row remove-margin">
                  <MdaeTextBox
                    id="txtadmissionSourceCode"
                    mdaeTextBoxTitle="Admit Source"
                    className="bottom-margin"
                    readOnly
                    mdaeDataUpdaterConfig={[
                      {
                        actionType: GET_CASE_CLAIM_DETAILS,
                        isAsyncComplete: true,
                        widgetOptionBindings: {
                          value: "admissionSourceCode",
                        },
                      },
                    ]}
                  />
                </div>
                <div className="row remove-margin">
                  <MdaeTextBox
                    id="txtdischargeStatus"
                    mdaeTextBoxTitle="Discharge Status"
                    className="bottom-margin"
                    readOnly
                    mdaeDataUpdaterConfig={[
                      {
                        actionType: GET_CASE_CLAIM_DETAILS,
                        isAsyncComplete: true,
                        widgetOptionBindings: {
                          value: "dischargeStatus",
                        },
                      },
                    ]}
                  />
                </div>
                <div className="row remove-margin">
                  <MdaeTextBox
                    id="txtAdmitType"
                    mdaeTextBoxTitle="Admit Type"
                    className="bottom-margin"
                    readOnly
                    mdaeDataUpdaterConfig={[
                      {
                        actionType: GET_CASE_CLAIM_DETAILS,
                        isAsyncComplete: true,
                        widgetOptionBindings: {
                          value: "admissionTypeCode",
                        },
                      },
                    ]}
                  />
                </div>
              </>
            )}
            {(caseFieldsTabFlags.isInPatient ||
              caseFieldsTabFlags.isOutPatient ||
              caseFieldsTabFlags.isProfessional) && (
              <div
                className="row remove-margin"
                title={completeCaseDetail.billingProviderNtcs}
              >
                <MdaeTextBox
                  id="txtBillingProvider"
                  mdaeTextBoxTitle=" Billing Provider:"
                  className="bottom-margin"
                  readOnly
                  mdaeDataUpdaterConfig={[
                    {
                      actionType: GET_CASE_CLAIM_DETAILS,
                      isAsyncComplete: true,
                      widgetOptionBindings: {
                        value: "billingProvider",
                      },
                    },
                  ]}
                />
              </div>
            )}
            {(caseFieldsTabFlags.isInPatient ||
              caseFieldsTabFlags.isOutPatient) && (
              <div
                className="row remove-margin"
                title={completeCaseDetail.attendingProviderNtcs}
              >
                <MdaeTextBox
                  id="txtAttendingProvider"
                  mdaeTextBoxTitle="Attending Provider"
                  className="bottom-margin"
                  readOnly
                  mdaeDataUpdaterConfig={[
                    {
                      actionType: GET_CASE_CLAIM_DETAILS,
                      isAsyncComplete: true,
                      widgetOptionBindings: {
                        value: "attendingProvider",
                      },
                    },
                  ]}
                />
              </div>
            )}
            <div
              className="row remove-margin"
              title={completeCaseDetail.renderingProviderNtcs}
            >
              <MdaeTextBox
                id="txtRenderingProvider"
                mdaeTextBoxTitle="Rendering Provider"
                className="bottom-margin"
                readOnly
                mdaeDataUpdaterConfig={[
                  {
                    actionType: GET_CASE_CLAIM_DETAILS,
                    isAsyncComplete: true,
                    widgetOptionBindings: {
                      value: "renderingProvider",
                    },
                  },
                ]}
              />
            </div>{" "}
            <div className="fa-inline-line-height">
              <span className="cpt-inline-font-style">
                CPT &reg; only &copy; {currentCptYear} American Medical
                Association. All Rights Reserved.
              </span>
            </div>
          </div>
          <div className="col-xs-9" style={{ paddingLeft: 0 }}>
            <div className="row remove-margin">
              <div className="col-xs-8" style={{ paddingLeft: 0 }}>
                <div className="col-xs-12">
                  <div className="justify-items-space-between">
                    <div className="mdae-data-field-label">Payer</div>
                    {/* Add button here */}
                  </div>
                  <MdaeDataGrid
                    className="bottom-margin"
                    {...payerDatagridProps}
                  />
                </div>
              </div>
            </div>
            <div className="row remove-margin">
              <div className="col-xs-4" style={{ paddingLeft: 0 }}>
                {" "}
                {!caseFieldsTabFlags.isProfessional && (
                  <div className="col-xs-12">
                    <div className="justify-items-space-between">
                      <div className="mdae-data-field-label">Occurrence</div>
                      {/* Add button here */}
                    </div>
                    <MdaeDataGrid
                      id="grdOccurrence"
                      className="bottom-margin"
                      {...occuranceDatagridProps}
                    />
                  </div>
                )}
              </div>
              <div className="row remove-margin">
                <div
                  className="col-xs-4"
                  style={{ marginBottom: 10, marginTop: 10 }}
                >
                  <div className="justify-items-space-between">
                    <div className="mdae-data-field-label">Condition</div>
                    {/* Add button here */}
                  </div>
                  <MdaeDataGrid
                    id="grdCondition"
                    className="bottom-margin"
                    {...conditionDatagridProps}
                  />
                </div>
              </div>
              <div className="col-xs-4">
                {" "}
                {caseFieldsTabFlags.isInPatient && (
                  <div className="col-xs-12">
                    <div className="justify-items-space-between">
                      <div className="mdae-data-field-label">
                        Occurrence Span
                      </div>
                      {/* Add button here */}
                    </div>
                    <MdaeDataGrid
                      id="grdOccurrenceSpan"
                      className="bottom-margin"
                      {...occuranceSpanDatagridProps}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* <div className="row remove-margin">

        </div> */}
      </div>
    </div>
  );
};
const getDiagnosisDatagridConfigs = (
  caseData,
  completeCaseDetail,
  diagnosisCheckboxCellRender,
  diagnosisCellPrepared,
  isVisibleFromImportCharges,
  isCheckBoxVisibleForCharges
) => {
  let diagnosisCheckbox = {
      caption: "",
      dataField: "diagnosisCheckbox",
      width: 50,
      fixed: true,
      cellRender: diagnosisCheckboxCellRender
  };
  let diagnosisCurrentConfigs: any = {};
  diagnosisCurrentConfigs.claimType = caseData.claimType;
  diagnosisCurrentConfigs._ClaimType = "";
  diagnosisCurrentConfigs.patientTypeName = "";
  diagnosisCurrentConfigs.columns = [];
  diagnosisCurrentConfigs.gridHeight = 50;
  switch (diagnosisCurrentConfigs.claimType?.toUpperCase()) {
    case "PROFESSIONAL":
      diagnosisCurrentConfigs._ClaimType = diagnosisCurrentConfigs.claimType;
      break;
    case "HOSPITAL":
      diagnosisCurrentConfigs._ClaimType = diagnosisCurrentConfigs.claimType;
      break;
    case "OUTPATIENT":
      diagnosisCurrentConfigs._ClaimType = "HOSPITAL";
      break;
    case "INPATIENT":
      diagnosisCurrentConfigs._ClaimType = "HOSPITAL";
      break;
  }
  if (diagnosisCurrentConfigs._ClaimType?.toUpperCase() === "HOSPITAL") {
    diagnosisCurrentConfigs.patientTypeName =
      completeCaseDetail.patientType !== "OP" ? "- Inpatient" : "- Outpatient";
    diagnosisCurrentConfigs.claimType =
      completeCaseDetail.patientType !== "OP" ? "INPATIENT" : "OUTPATIENT";
   }
  switch (diagnosisCurrentConfigs.claimType?.toUpperCase()) {
    case "INPATIENT":
      diagnosisCurrentConfigs.columns = diagnosisConfigs.InPatient.List;
      diagnosisCurrentConfigs.gridHeight = 300;
      break;
    case "OUTPATIENT":
      diagnosisCurrentConfigs.columns = diagnosisConfigs.OutPatient.List;
      diagnosisCurrentConfigs.gridHeight = 300;
      break;
    case "PROFESSIONAL":
      diagnosisCurrentConfigs.columns = diagnosisConfigs.Professional.List;
      diagnosisCurrentConfigs.gridHeight = 250;
      break;
  }
  const diagnosisDatagridConfigs: IMdaeDataGridProps = {
    id: "grdDiagnoses",
    mdaeColumns: isVisibleFromImportCharges || isCheckBoxVisibleForCharges
    ? [diagnosisCheckbox, ...diagnosisCurrentConfigs.columns]
    : [...diagnosisCurrentConfigs.columns],
    height: diagnosisCurrentConfigs.gridHeight,
    paging: { enabled: false },
    onCellPrepared: diagnosisCellPrepared,
    mdaeDataUpdaterConfig: [
      {
        actionType: GET_CASE_CLAIM_DETAILS,
        isAsyncComplete: true,
        widgetOptionBindings: {
          dataSource: "diagnosesCaseCodes",
        },
      },
    ],
  };
  return {diagnosisDatagridConfigs, diagnosisCurrentConfigs};
};
const getExternalCauseOfInjuryDatagridProps = (completeCaseDetail) => {
  const externalInjuryCodes = completeCaseDetail?.externalInjuryCodes;
  const externalCauseOfInjuryDataGridConfig: IMdaeDataGridProps = {
    id: "grdExternalCauseOfInjury",
    headerFilter: { visible: false },
    height: getHeight(externalInjuryCodes),
    filterRow: { visible: false },
    selection: { mode: "single" },
    paging: {
      pageSize: 6,
    },
    pager: {
      showInfo: false,
    },
    hoverStateEnabled: true,
    searchPanel: {
      visible: false,
      width: 240,
      placeholder: "Search...",
    },
    mdaeColumns: [
      {
        dataField: "codePositionNo",
        width: 40,
        caption: "",
      },
      {
        dataField: "codeValue",
        caption: "Code",
        width: 355,
        allowFiltering: false,
        allowHeaderFiltering: false,
        // cellTemplate: "CodeValueTemplate",
        cellRender: codeValueTemplate,
      },
    ],
    noDataText: "",
    mdaeDataUpdaterConfig: [
      {
        actionType: GET_CASE_CLAIM_DETAILS,
        isAsyncComplete: true,
        widgetOptionBindings: {
          dataSource: "externalInjuryCodes",
        },
      },
    ],
  };
  return externalCauseOfInjuryDataGridConfig;
};
const getHeight = (list) => {
  if (list != undefined) {
    if (list?.length > 0) {
      return "auto";
    } else {
      return "50px";
    }
  } else {
    return "50px";
  }
};
const getVisitReasonDatagridProps = (completeCaseDetail) => {
  const visitReasonCodes = completeCaseDetail?.visitReasonCodes;
  const visitReasonDataGridConfig: IMdaeDataGridProps = {
    id: "grdVisitReason",
    height: () => getHeight(visitReasonCodes),
    headerFilter: { visible: false },
    filterRow: { visible: false },
    selection: { mode: "single" },
    paging: {
      pageSize: 6,
    },
    pager: {
      showInfo: false,
    },
    hoverStateEnabled: true,
    mdaeColumns: [
      {
        dataField: "codePositionNo",
        width: 40,
        caption: "",
      },
      {
        dataField: "codeValue",
        caption: "Code",
        allowFiltering: false,
        width: 355,
        allowHeaderFiltering: false,
        cellRender: codeValueTemplate,
      },
    ],
    noDataText: "",
    mdaeDataUpdaterConfig: [
      {
        actionType: GET_CASE_CLAIM_DETAILS,
        isAsyncComplete: true,
        widgetOptionBindings: {
          dataSource: "visitReasonCodes",
        },
      },
    ],
  };
  return visitReasonDataGridConfig;
};
const getICDProceduresDatagridProps = (completeCaseDetail, icdProcedureCheckboxCellRender?, icdProcedureCellPrepared?, isVisibleFromImportCharges?, isCheckBoxVisibleForCharges?) => {
  const icdProcedureCodes = completeCaseDetail.icdProcedureCodes;
  let icdProcedureCheckbox = {
    caption: "",
    dataField: "icdProcedureCheckbox",
    width: 50,
    fixed: true,
    cellRender: icdProcedureCheckboxCellRender,
  };
  const baseColumns = [
      {
        dataField: "codePositionNo",
        width: 40,
        caption: "",
      },
      {
        dataField: "codeValue",
        caption: "Code",
        allowFiltering: false,
        allowHeaderFiltering: false,
        cellRender: codeValueTemplate,
        width: 253,
      },
      {
        dataField: "codeFromDate",
        caption: "Date",
        dataType: "date",
        format: "MM/dd/yyyy",
        allowFiltering: false,
        allowHeaderFiltering: false,
        width: 103,
      },
  ];
  const iCDProceduresDataGridConfig: IMdaeDataGridProps = {
    id: "grdICDProcedures",
    height: () => getHeight(icdProcedureCodes),
    headerFilter: { visible: false },
    filterRow: { visible: false },
    selection: { mode: "single" },
    paging: {
      pageSize: 6,
    },
    pager: {
      showInfo: false,
    },
    hoverStateEnabled: true,
    searchPanel: {
      visible: false,
      width: 240,
      placeholder: "Search...",
    },
    mdaeColumns: isVisibleFromImportCharges || isCheckBoxVisibleForCharges
      ? [icdProcedureCheckbox, ...baseColumns]
      : [...baseColumns],
    onCellPrepared: icdProcedureCellPrepared,
    noDataText: "",
    mdaeDataUpdaterConfig: [
      {
        actionType: GET_CASE_CLAIM_DETAILS,
        isAsyncComplete: true,
        widgetOptionBindings: {
          dataSource: "icdProcedureCodes",
        },
      },
    ],
  };
  return iCDProceduresDataGridConfig;
};
const getLinesDatagridProps = (
  caseFieldsTabFlags,
  isLineDataGridShowAllField,
  isDenialPredictorFlagAvailable,
  cellLineCellTemplate,
  isShowChkbxViewCmpnt,
  dispatch,
  aliasBillDataFields,
  isVisibleFromImportCharges,
  isCheckBoxVisibleForCharges,
  getAllRoundCharges,
  toggleConfirmationPopupVisible,
  isComponentRowsAvailable
) => {
  let linesCurrentConfigs: {
    columns?: any[];
    extraColumns?: any[];
    pager?: object;
  } = {};
    let linesCheckbox = {
    caption: "",
      dataField: "lineCheckbox",
      width: 50,
      fixed: true,
      cellRender: (e)=>dispatch(linesCheckboxCellRender(e,isCheckBoxVisibleForCharges, getAllRoundCharges, toggleConfirmationPopupVisible))
  };
  let showCompositeRevenuecodesColumn = [
    {
      dataField: "parentCaseLineId",
      caption: "",
      cellRender: cellLineCellTemplate,
      fixed: true,
      width: 40,
      visible: isComponentRowsAvailable,
      visibleIndex: 1,
    },
  ];
  if (caseFieldsTabFlags.isInPatient || caseFieldsTabFlags.isOutPatient) {
    linesCurrentConfigs.columns = isShowChkbxViewCmpnt
      ? [
          ...linesConfig.InOutPatient.Columns,
          ...showCompositeRevenuecodesColumn,
        ]
      : linesConfig.InOutPatient.Columns;
    if (isDenialPredictorFlagAvailable) {
      linesCurrentConfigs.columns[0].visible = true;
    } else {
      linesCurrentConfigs.columns[0].visible = false;
    }
      linesCurrentConfigs.extraColumns =
          getlinesInOutExtraColumns(aliasBillDataFields);
    linesCurrentConfigs.pager = linesConfig.InOutPatient.pager;
  } else if (caseFieldsTabFlags.isProfessional) {
    linesCurrentConfigs.columns = linesConfig.Professional.Columns;
    if (isDenialPredictorFlagAvailable) {
      linesCurrentConfigs.columns[0].visible = true;
    } else {
      linesCurrentConfigs.columns[0].visible = false;
    }
    linesCurrentConfigs.extraColumns =
          getLinesProfessionalExtraColumns(aliasBillDataFields);
  }
  linesCurrentConfigs.columns = Array.isArray(linesCurrentConfigs.columns)
  ? (isVisibleFromImportCharges || isCheckBoxVisibleForCharges
      ? [linesCheckbox, ...linesCurrentConfigs.columns]
      : [...linesCurrentConfigs.columns])
  : linesCurrentConfigs.columns;

  const linesDataGridConfig: IMdaeDataGridProps = {
    id: "grdLines",
    mdaeColumns: isLineDataGridShowAllField
      ? [...linesCurrentConfigs.columns, ...linesCurrentConfigs.extraColumns]
      : linesCurrentConfigs.columns,
    // pager: linesCurrentConfigs.pager,
    headerFilter: { visible: false },
    filterRow: { visible: false },
    selection: { mode: "single" },
    hoverStateEnabled: true,
    columnFixing: {
      enabled: true,
    },
    pager: {
      showPageSizeSelector: true,
      allowedPageSizes: [10, 20, 30],
      showInfo: true,
    },
    searchPanel: {
      visible: false,
      width: 240,
      placeholder: "Search...",
    },
    noDataText: "",
  };
  return linesDataGridConfig;
};
const getPayerDatagridProps = (completeCaseDetail) => {
  const auditCaseParticipants = completeCaseDetail.auditCaseParticipants;
  const payerDataGridConfig: IMdaeDataGridProps = {
    id: "grdPayer",
    height: () => getHeight(auditCaseParticipants),
    headerFilter: { visible: false },
    filterRow: { visible: false },
    selection: { mode: "single" },
    paging: {
      pageSize: 6,
    },
    pager: {
      showInfo: false,
    },
    hoverStateEnabled: true,
    mdaeColumns: [
      {
        dataField: "destinationPayerInd",
        caption: "Bill To",
        width: 60,
        allowFiltering: false,
        allowHeaderFiltering: false,
        // cellTemplate: "destinationPayerIndTemplate",
        alignment: "center",
      },
      {
        dataField: "payorResponsibilitySeqNo",
        caption: "",
        allowFiltering: false,
        width: 24,
        allowHeaderFiltering: false,
        // cellTemplate: "payorResponsibilitySeqNoTemplate",
        alignment: "center",
      },
      {
        dataField: "participantName",
        caption: "Payer",
        allowFiltering: false,
        width: 280,
        allowHeaderFiltering: false,
        cellTemplate: function(container, options) {
          $("<div>")
            .text(options.data.participantName)
            .attr("title", options.data.participantName)
            .appendTo(container);
        },
      },
      {
        dataField: "claimFilingIndCd",
        caption: "Payer Type",
        allowFiltering: false,
        allowHeaderFiltering: false,
        // cellTemplate: "claimFilingIndCdTemplate",
        width: 82,
      },
      {
        dataField: "payerPaidAmt",
        caption: "Prior Pmt",
        allowFiltering: false,
        allowHeaderFiltering: false,
        // cellTemplate: "payerPaidAmtTemplate",
        width: 140,
        alignment: "right",
      },
    ],
    noDataText: "",
    mdaeDataUpdaterConfig: [
      {
        actionType: GET_CASE_CLAIM_DETAILS,
        isAsyncComplete: true,
        widgetOptionBindings: {
          dataSource: "auditCaseParticipants",
        },
      },
    ],
  };
  return payerDataGridConfig;
};
const getOccuranceDatagridProps = (completeCaseDetail) => {
  const occurrenceCodes = completeCaseDetail.occurrenceCodes;
  const occuranceDataGridConfig: IMdaeDataGridProps = {
    id: "grdOccurance",
    height: () => getHeight(occurrenceCodes),
    headerFilter: { visible: false },
    filterRow: { visible: false },
    selection: { mode: "single" },
    paging: {
      pageSize: 6,
    },
    pager: {
      showInfo: false,
    },
    hoverStateEnabled: true,
    mdaeColumns: [
      {
        dataField: "codeValue",
        caption: "Code",
        allowFiltering: false,
        allowHeaderFiltering: false,
        cellRender: codeValueTemplate,
        // cellTemplate: "codeValueTemplate",
        width: 134,
      },
      {
        dataField: "codeFromDate",
        caption: "Date",
        allowFiltering: false,
        allowHeaderFiltering: false,
        dataType: "date",
        format: "MM/dd/yyyy",
        // cellTemplate: "codeFromDateTemplate",
        width: 134,
      },
    ],
    noDataText: "",
    mdaeDataUpdaterConfig: [
      {
        actionType: GET_CASE_CLAIM_DETAILS,
        isAsyncComplete: true,
        widgetOptionBindings: {
          dataSource: "occurrenceCodes",
        },
      },
    ],
  };
  return occuranceDataGridConfig;
};
const getConditionDatagridProps = (completeCaseDetail) => {
  const conditionCodes = completeCaseDetail.conditionCodes;
  const conditionDataGridConfig: IMdaeDataGridProps = {
    id: "grdCondition",
    height: () => getHeight(conditionCodes),
    headerFilter: { visible: false },
    filterRow: { visible: false },
    selection: { mode: "single" },
    paging: {
      pageSize: 6,
    },
    pager: {
      showInfo: false,
    },
    hoverStateEnabled: true,
    mdaeColumns: [
      {
        dataField: "codeValue",
        caption: "Code",
        allowFiltering: false,
        allowHeaderFiltering: false,
        // cellTemplate: "codeValueTemplate",
        cellRender: codeValueTemplate,
        width: 253,
      },
    ],
    noDataText: "",
    mdaeDataUpdaterConfig: [
      {
        actionType: GET_CASE_CLAIM_DETAILS,
        isAsyncComplete: true,
        widgetOptionBindings: {
          dataSource: "conditionCodes",
        },
      },
    ],
  };
  return conditionDataGridConfig;
};
const getOccuranceSpanDatagridProps = (completeCaseDetail) => {
  const occurrenceSpanCodes = completeCaseDetail.occurrenceSpanCodes;
  const occuranceSpanDataGridConfig: IMdaeDataGridProps = {
    id: "grdOccuranceSpan",
    height: () => getHeight(occurrenceSpanCodes),
    headerFilter: { visible: false },
    filterRow: { visible: false },
    selection: { mode: "single" },
    paging: {
      pageSize: 6,
    },
    pager: {
      showInfo: false,
    },
    hoverStateEnabled: true,
    mdaeColumns: [
      {
        dataField: "codeValue",
        caption: "Code",
        allowFiltering: false,
        allowHeaderFiltering: false,
        // cellTemplate: "codeValueTemplate",
        cellRender: codeValueTemplate,
        width: 85,
      },
      {
        dataField: "codeFromDate",
        caption: "From Date",
        allowFiltering: false,
        allowHeaderFiltering: false,
        width: 85,
      },
      {
        dataField: "codeToDate",
        caption: "Thru Date",
        allowFiltering: false,
        allowHeaderFiltering: false,
        width: 85,
      },
    ],
    noDataText: "",
    mdaeDataUpdaterConfig: [
      {
        actionType: GET_CASE_CLAIM_DETAILS,
        isAsyncComplete: true,
        widgetOptionBindings: {
          dataSource: "occurrenceSpanCodes",
        },
      },
    ],
  };
  return occuranceSpanDataGridConfig;
};
export default CaseFields;
